import { LogOut, Menu } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { UserAvatar } from '@/components/ui/user-avatar'
import { useAuthStatus, useSignOut } from '@/models/auth'
import { useCurrentUser } from '@/models/user'

type AccountMenuTrigger = 'avatar' | 'menu'

/** `menu` swaps the avatar for a menu icon, for navs where another face (a matched broker) should lead. */
export function LoginButtons({ trigger = 'avatar' }: { trigger?: AccountMenuTrigger } = {}) {
  const { data: authStatus } = useAuthStatus()
  // enabled: false — read whatever the layout already fetched rather than
  // firing a second /api/me for the avatar.
  const { user } = useCurrentUser({ enabled: false })
  const signOut = useSignOut()

  if (authStatus && !authStatus.authenticated) {
    return <Button href="/insure">Sign in</Button>
  }
  if (!user) return null

  return (
    <AccountMenu
      name={`${user.firstName} ${user.lastName}`.trim() || user.email}
      email={user.email}
      photo={user.photo}
      trigger={trigger}
      onSignOut={() => signOut.mutate()}
    />
  )
}

/** The signed-in person's menu, without the session behind it. */
export function AccountMenu({
  name,
  email,
  photo,
  trigger = 'avatar',
  onSignOut,
}: {
  name: string
  email: string
  photo?: string | null
  trigger?: AccountMenuTrigger
  onSignOut: () => void
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger === 'menu' ? (
          <Button variant="ghost" size="icon-sm" className="rounded-full" aria-label="Account menu">
            <Menu />
          </Button>
        ) : (
          <Button variant="iconOnly" size="icon" className="rounded-full" aria-label="Account menu">
            <UserAvatar name={name} src={photo} />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="ph-no-capture flex flex-col gap-0.5">
          <span className="truncate">{name}</span>
          <span className="text-muted-foreground truncate text-xs font-normal">{email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onSignOut}>
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
